import { useEffect, type ReactNode } from 'react';
import { NavLink, Routes, Route, useLocation, useNavigate } from 'react-router';

export interface TabItem {
  label: string;
  count?: number;
  link: string;
  component: ReactNode;
}

interface TabsProps {
  tabs: TabItem[];
  basePath: string;
  title: string;
  onTabChange?: (tabId: string) => void;
}

export function Tabs({ tabs, onTabChange, basePath, title }: TabsProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (lastSegment(pathname) !== basePath) return;
    const firstLink = tabs.find((t) => t.link)?.link ?? '';
    const firstTabLink = normalize(firstLink, pathname, basePath);
    navigate(firstTabLink);
  }, [basePath, navigate, pathname, tabs]);

  return (
    <div className="w-full">
      <div className="w-full bg-neut-bg-alt rounded-lg shadow-sm border border-neut-border">
        <div className="border-b border-neut-border px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl text-neut-prim font-semibold">{title}</h1>
          </div>
        </div>

        <div className="flex items-center gap-2 px-6 py-2 overflow-x-auto">
          {tabs.map((tab) => (
            <NavLink
              key={tab.link}
              to={normalize(tab.link, pathname, basePath)}
              onClick={() => onTabChange?.(tab.link)}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all text-sm font-medium 
                  ${
                    isActive
                      ? 'bg-tab-active-bg text-tab-active-text'
                      : 'text-neut-prim hover:bg-neut-hover'
                  }`
              }
              style={({ isActive }) =>
                ({
                  '--tab-count-bg': isActive
                    ? 'var(--color-tab-count-active-bg)'
                    : 'var(--color-neut-100)',
                  '--tab-count-color': isActive
                    ? 'var(--color-tab-count-active-text)'
                    : 'var(--color-neut-500)',
                }) as React.CSSProperties
              }
            >
              <span>{tab.label}</span>
              {typeof tab.count !== 'undefined' && (
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium`}
                  style={{
                    backgroundColor: 'var(--tab-count-bg)',
                    color: 'var(--tab-count-color)',
                  }}
                >
                  {tab.count}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </div>
      <Routes>
        {tabs.map((t) => (
          <Route key={t.link} path={`${t.link}/*`} element={t.component} />
        ))}
      </Routes>
    </div>
  );
}

const lastSegment = (pathname: string): string => {
  const clean = pathname.split('?')[0].split('#')[0].replace(/\/+$/, '');
  const segments = clean.split('/').filter(Boolean);
  return segments.pop() ?? '';
};

const normalize = (link: string, pathname: string, basePath?: string) => {
  const cleanLink = link.replace(/^\/+/, '');
  const cleanPathname = pathname.replace(/\/+$/, '');

  if (!basePath) {
    return `${cleanPathname}/${cleanLink}`.replace(/\/+/g, '/');
  }

  const cleanBase = basePath.replace(/^\/+|\/+$/g, '');
  const segments = cleanPathname.split('/').filter(Boolean);
  const idx = segments.lastIndexOf(cleanBase);

  // If basePath exists in the current path, slice up to it (inclusive); else use full pathname.
  const head =
    idx !== -1
      ? `/${segments.slice(0, idx + 1).join('/')}`
      : cleanPathname || '/';

  return `${head}/${cleanLink}`.replace(/\/+/g, '/');
};
